import React from 'react';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '../data/companyInfo';
import { social } from '../data/siteMeta';
import { NAV_LABELS, ROUTE_MAP } from '../data/routes';

export const SITE_URL = 'https://demart.com.tr';
const DEFAULT_OG_IMAGE_PATH = '/images/brand/logo.webp';

const seoContent = {
  tr: {
    keywords:
      'vana kilitleri, valve interlocks, taşınabilir aktüatör, portable actuator, vana bakım, valve maintenance, Sofis, Smith Flow Control, Netherlocks, EasiDrive, Power Wrench, pozisyoner, kontrol vanası, küresel vana, kelebek vana, aktüatör, İstanbul, Türkiye, endüstriyel vana'
  },
  en: {
    keywords:
      'valve interlocks, portable actuator, valve maintenance, Sofis, Smith Flow Control, Netherlocks, EasiDrive, Power Wrench, position indicator, control valve, ball valve, butterfly valve, actuator, Istanbul, Turkey, industrial valve'
  },
  az: {
    keywords:
      'vana kilidləri, valve interlocks, daşınan aktuator, portable actuator, vana texniki xidmət, Sofis, Smith Flow Control, Netherlocks, EasiDrive, Power Wrench, İstanbul, Türkiyə, sənaye vanası'
  }
};

const localeMap = { tr: 'tr_TR', en: 'en_US', az: 'az_AZ' };
const localeLanguageCodeMap = { tr: 'tr-TR', en: 'en-US', az: 'az-Latn-AZ' };
const localeTitleSuffix = {
  tr: 'DEMART TR',
  en: 'DEMART EN',
  az: 'DEMART AZ'
};
const MAX_TITLE_LENGTH = 60;
const globalAreaServed = [
  { '@type': 'Country', name: 'Turkey' },
  { '@type': 'Country', name: 'Azerbaijan' }
];

const toAbsoluteUrl = (value) => {
  if (!value) return '';
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  const normalizedPath = value.startsWith('/') ? value : `/${value}`;
  return `${SITE_URL}${normalizedPath}`;
};

const toPreloadHref = (value) => {
  if (!value) return '';
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return value.startsWith('/') ? value : `/${value}`;
};

const normalizePath = (value) => {
  if (!value) return '/';
  if (value.startsWith('http://') || value.startsWith('https://')) {
    const parsed = new URL(value);
    return parsed.pathname + parsed.search;
  }
  return value.startsWith('/') ? value : `/${value}`;
};

const buildSeoTitle = (pageTitle, locale) => {
  const cleanTitle = String(pageTitle || '').replace(/\s+/g, ' ').trim();
  const suffix = localeTitleSuffix[locale] || 'DEMART';
  const titleTail = ` | ${suffix}`;

  if (!cleanTitle) return suffix;
  const full = `${cleanTitle}${titleTail}`;
  if (full.length <= MAX_TITLE_LENGTH) return full;

  const maxTitlePart = Math.max(16, MAX_TITLE_LENGTH - titleTail.length - 3);
  const trimmedTitle = cleanTitle.slice(0, maxTitlePart).trimEnd();
  return `${trimmedTitle}...${titleTail}`;
};

const getRouteKeyFromPath = (locale, normalizedPath) => {
  const routes = ROUTE_MAP[locale] || ROUTE_MAP.tr;
  return Object.keys(routes).find((key) => routes[key] === normalizedPath) || null;
};

const getParentRouteKey = (locale, normalizedPath) => {
  const routes = ROUTE_MAP[locale] || ROUTE_MAP.tr;
  const parentCandidates = ['products', 'services', 'solutions', 'applications', 'industries', 'news', 'docs'];

  for (const key of parentCandidates) {
    const parentPath = routes[key];
    if (!parentPath || parentPath === '/' || normalizedPath === parentPath) continue;
    if (normalizedPath.startsWith(`${parentPath}/`)) return key;
  }

  return null;
};

const buildBreadcrumbItems = ({ locale, canonicalPath, title }) => {
  const routes = ROUTE_MAP[locale] || ROUTE_MAP.tr;
  const labels = NAV_LABELS[locale] || NAV_LABELS.tr;
  const items = [];
  const homePath = routes.home || '/';

  items.push({
    name: labels.home || 'Home',
    item: toAbsoluteUrl(homePath)
  });

  if (canonicalPath === homePath) return items;

  const exactKey = getRouteKeyFromPath(locale, canonicalPath);
  if (exactKey && exactKey !== 'home') {
    items.push({
      name: labels[exactKey] || title,
      item: toAbsoluteUrl(canonicalPath)
    });
    return items;
  }

  const parentKey = getParentRouteKey(locale, canonicalPath);
  if (parentKey && routes[parentKey]) {
    items.push({
      name: labels[parentKey] || title,
      item: toAbsoluteUrl(routes[parentKey])
    });
  }

  items.push({
    name: title,
    item: toAbsoluteUrl(canonicalPath)
  });

  const seen = new Set();
  return items.filter((item) => {
    if (seen.has(item.item)) return false;
    seen.add(item.item);
    return true;
  });
};

const SEO = ({
  title,
  description,
  urlPath,
  imagePath,
  preloadImagePath,
  preloadImagePaths,
  lang,
  alternates,
  keywords,
  type = 'website',
  article = null
}) => {
  const missing = [];
  if (!title) missing.push('title');
  if (!description) missing.push('description');
  if (!urlPath) missing.push('urlPath');

  if (missing.length > 0 && process.env.NODE_ENV !== 'production') {
    const message = `[SEO] Missing required props: ${missing.join(', ')} for route "${urlPath || 'unknown'}"`;
    throw new Error(message);
  }

  const locale = lang || 'tr';
  const company = companyInfo[locale] || companyInfo.tr;
  const canonicalPath = normalizePath(urlPath);
  const canonicalUrl = toAbsoluteUrl(canonicalPath);
  const metaImage = toAbsoluteUrl(imagePath || DEFAULT_OG_IMAGE_PATH);
  const preloadCandidates = [preloadImagePath, ...(preloadImagePaths || [])]
    .filter(Boolean)
    .map((item) => toPreloadHref(item));
  const preloadUnique = [...new Set(preloadCandidates)];
  const fullTitle = buildSeoTitle(title, locale);
  const localeTag = localeMap[locale] || 'tr_TR';
  const languageNames = { tr: 'Turkish', en: 'English', az: 'Azerbaijani' };
  const languageName = languageNames[locale] || 'Turkish';
  const defaultKeywords = seoContent[locale]?.keywords || seoContent.tr.keywords;
  const breadcrumbItems = buildBreadcrumbItems({
    locale,
    canonicalPath,
    title
  });

  const resolvedAlternates = {
    tr: toAbsoluteUrl(alternates?.tr || canonicalPath),
    en: toAbsoluteUrl(alternates?.en || canonicalPath),
    az: toAbsoluteUrl(alternates?.az || canonicalPath)
  };
  const xDefault = toAbsoluteUrl(alternates?.['x-default'] || alternates?.tr || canonicalPath);
  const ogLocaleAlternates = Object.entries(localeMap)
    .filter(([langCode]) => langCode !== locale)
    .map(([, ogLocale]) => ogLocale);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: company.fullName,
    alternateName: company.name,
    url: SITE_URL,
    logo: toAbsoluteUrl('/images/brand/logo.webp'),
    description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: company.address.street,
      addressLocality: company.address.district,
      addressRegion: company.address.city,
      postalCode: company.address.postalCode,
      addressCountry: 'TR'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: company.phone,
      email: company.email,
      contactType: 'customer service',
      areaServed: ['TR', 'AZ'],
      availableLanguage: ['Turkish', 'English', 'Azerbaijani']
    },
    sameAs: [social.linkedin, social.youtube]
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#localbusiness`,
    name: company.fullName,
    image: metaImage,
    url: SITE_URL,
    telephone: company.phone,
    email: company.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: company.address.street,
      addressLocality: company.address.district,
      addressRegion: company.address.city,
      postalCode: company.address.postalCode,
      addressCountry: 'TR'
    },
    areaServed: globalAreaServed,
    parentOrganization: {
      '@id': `${SITE_URL}/#organization`
    },
    sameAs: [social.linkedin, social.youtube]
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: company.name,
    inLanguage: [localeLanguageCodeMap.tr, localeLanguageCodeMap.en, localeLanguageCodeMap.az],
    publisher: {
      '@id': `${SITE_URL}/#organization`
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.item
    }))
  };

  const articleSchema = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description,
        inLanguage: localeLanguageCodeMap[locale] || localeLanguageCodeMap.tr,
        datePublished: article.publishedTime,
        dateModified: article.modifiedTime || article.publishedTime,
        image: [metaImage],
        mainEntityOfPage: canonicalUrl,
        author: {
          '@type': 'Organization',
          name: article.author || company.fullName
        },
        publisher: {
          '@id': `${SITE_URL}/#organization`
        }
      }
    : null;

  const schemaList = [organizationSchema, localBusinessSchema, websiteSchema];
  if (breadcrumbItems.length > 1 || canonicalPath !== '/') schemaList.push(breadcrumbSchema);
  if (articleSchema) schemaList.push(articleSchema);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content={company.fullName} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <link rel="canonical" href={canonicalUrl} />
      {preloadUnique.map((url, index) => (
        <link key={`preload-${url}`} rel="preload" as="image" href={url} fetchPriority={index === 0 ? 'high' : undefined} />
      ))}

      <link rel="alternate" hrefLang="tr" href={resolvedAlternates.tr} />
      <link rel="alternate" hrefLang="en" href={resolvedAlternates.en} />
      <link rel="alternate" hrefLang="az" href={resolvedAlternates.az} />
      <link rel="alternate" hrefLang="x-default" href={xDefault} />

      <html lang={locale} />
      <meta name="language" content={languageName} />
      <meta name="geo.region" content="TR-34" />
      <meta name="geo.placename" content="Istanbul" />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content={company.name} />
      <meta property="og:locale" content={localeTag} />
      {ogLocaleAlternates.map((value) => (
        <meta key={`og-locale-alt-${value}`} property="og:locale:alternate" content={value} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author} />
        </>
      )}

      {schemaList.map((schema, index) => (
        <script key={`jsonld-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
